import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { api, ApiError } from '../lib/api.js';
import { ShortCodeTag } from './ShortCodeTag.js';
import { Pagination } from './Pagination.js';
import { CompanyHoverBadge } from './CompanyHoverBadge.js';
import { Modal } from './Modal.js';
import type {
  CompanyAnalyticsItem,
  CompanyCertificatesResponse,
  CompanyCertificateItem,
} from '@gng-ehs/shared';

const FINANCIAL_YEARS = [
  { value: 'ALL', label: 'All Financial Years' },
  { value: '2026', label: 'FY 2026-27 (Apr 2026 – Mar 2027)' },
  { value: '2025', label: 'FY 2025-26 (Apr 2025 – Mar 2026)' },
  { value: '2024', label: 'FY 2024-25 (Apr 2024 – Mar 2025)' },
  { value: '2023', label: 'FY 2023-24 (Apr 2023 – Mar 2024)' },
  { value: '2022', label: 'FY 2022-23 (Apr 2022 – Mar 2023)' },
];

interface CompanyDrillDownViewProps {
  companyId: string;
  initialCompany?: CompanyAnalyticsItem;
  onBack: () => void;
  defaultFinancialYear?: string;
  defaultStartDate?: string;
  defaultEndDate?: string;
  defaultCertType?: 'ALL' | 'TRAINING' | 'FORM_9' | 'FORM_10' | 'FORM_11';
}

export function CompanyDrillDownView({
  companyId,
  initialCompany,
  onBack,
  defaultFinancialYear = 'ALL',
  defaultStartDate = '',
  defaultEndDate = '',
  defaultCertType = 'ALL',
}: CompanyDrillDownViewProps) {
  const [certType, setCertType] = useState<'ALL' | 'TRAINING' | 'FORM_9' | 'FORM_10' | 'FORM_11'>(defaultCertType);
  const [financialYear, setFinancialYear] = useState<string>(defaultFinancialYear);
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE'>('ALL');

  const [data, setData] = useState<CompanyCertificatesResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // In-place PDF Preview Modal state for certificates
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [selectedCert, setSelectedCert] = useState<CompanyCertificateItem | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [viewError, setViewError] = useState<string>('');
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);

  const handleOpenView = async (cert: CompanyCertificateItem) => {
    setSelectedCert(cert);
    setViewModalOpen(true);
    setViewLoading(true);
    setViewError('');

    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }

    try {
      let url = '';
      if (cert.type === 'FORM_9' || cert.type === 'FORM_10') {
        url = await (cert.type === 'FORM_9' ? api.form9 : api.form10).getPdfBlobUrl(cert.id);
      } else if (cert.type === 'FORM_11') {
        url = await api.form11.getPdfBlobUrl(cert.id);
      } else if (cert.type === 'TRAINING') {
        url = await api.training.getPdfBlobUrl(cert.id);
      }
      setPdfBlobUrl(url);
    } catch (err: unknown) {
      setViewError(err instanceof Error ? err.message : 'Failed to fetch certificate PDF document');
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseView = () => {
    setViewModalOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setSelectedCert(null);
    setViewError('');
  };

  const handleDownloadPdf = async (cert: CompanyCertificateItem) => {
    setDownloadingPdf(true);
    try {
      if (cert.type === 'FORM_9' || cert.type === 'FORM_10') {
        await (cert.type === 'FORM_9' ? api.form9 : api.form10).downloadPdf(cert.id);
      } else if (cert.type === 'FORM_11') {
        await api.form11.downloadPdf(cert.id);
      } else if (cert.type === 'TRAINING') {
        await api.training.downloadPdf(cert.id);
      }
    } catch {
      if (cert.pdf_r2_url) {
        window.open(cert.pdf_r2_url, '_blank');
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Pagination for certificates
  const [page, setPage] = useState<number>(1);
  const pageSize = 15;

  const fetchCertificates = useCallback(() => {
    setLoading(true);
    setError('');

    api.dashboard
      .companyCertificates({
        company_id: companyId,
        cert_type: certType,
        financial_year: financialYear !== 'ALL' ? financialYear : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: searchQuery.trim() || undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
      })
      .then((res) => {
        if (res.data) {
          setData(res.data);
          setPage(1);
        }
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to load company certificates');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [companyId, certType, financialYear, startDate, endDate, searchQuery, statusFilter]);

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  // Handle FY Change
  const handleFinancialYearChange = (fy: string) => {
    setFinancialYear(fy);
    if (fy === 'ALL') {
      setStartDate('');
      setEndDate('');
    } else {
      const year = parseInt(fy, 10);
      setStartDate(`${year}-04-01`);
      setEndDate(`${year + 1}-03-31`);
    }
  };

  // Quick Presets
  const handleQuickPreset = (preset: '30d' | '90d' | 'month' | 'fy' | 'all') => {
    const now = new Date();
    if (preset === 'all') {
      setFinancialYear('ALL');
      setStartDate('');
      setEndDate('');
    } else if (preset === '30d') {
      setFinancialYear('ALL');
      const past = new Date(now.getTime() - 30 * 86400000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(now.toISOString().slice(0, 10));
    } else if (preset === '90d') {
      setFinancialYear('ALL');
      const past = new Date(now.getTime() - 90 * 86400000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(now.toISOString().slice(0, 10));
    } else if (preset === 'month') {
      setFinancialYear('ALL');
      const y = now.getFullYear();
      const m = now.getMonth();
      const first = new Date(y, m, 1).toISOString().slice(0, 10);
      const last = new Date(y, m + 1, 0).toISOString().slice(0, 10);
      setStartDate(first);
      setEndDate(last);
    } else if (preset === 'fy') {
      const m = now.getMonth();
      const currentFyStart = m >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      setFinancialYear(String(currentFyStart));
      setStartDate(`${currentFyStart}-04-01`);
      setEndDate(`${currentFyStart + 1}-03-31`);
    }
  };

  // CSV Export for this company's filtered certificates
  const handleExportCSV = () => {
    if (!data || !data.certificates.length) return;
    const headers = [
      'Type',
      'Certificate / Report No',
      'Equipment / Course Title',
      'Serial / Tag / Trainee',
      'Examination / Training Date',
      'Next Due / Valid Upto',
      'Days Remaining',
      'Status',
      'Inspector / Trainer',
    ];

    const rows = data.certificates.map((cert) => [
      `"${cert.type}"`,
      `"${cert.certificate_number.replace(/"/g, '""')}"`,
      `"${(cert.equipment_name || '').replace(/"/g, '""')}"`,
      `"${(cert.equipment_identifier || cert.trainee_name || '').replace(/"/g, '""')}"`,
      `"${cert.issue_or_exam_date}"`,
      `"${cert.expiry_or_due_date}"`,
      cert.days_until_due,
      `"${cert.status}"`,
      `"${(cert.inspector_or_trainer || '').replace(/"/g, '""')}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    const code = data.company.short_code || 'company';
    link.setAttribute('download', `${code}-certificates-report-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const certificates = data?.certificates || [];
  const totalPages = Math.ceil(certificates.length / pageSize);
  const paginatedCertificates = useMemo(() => {
    const start = (page - 1) * pageSize;
    return certificates.slice(start, start + pageSize);
  }, [certificates, page]);

  const company = data?.company || initialCompany;
  const summary = data?.summary || {
    total: 0,
    form9: 0,
    form10: 0,
    form11: 0,
    training: 0,
    compliant: 0,
    expiring_soon: 0,
    overdue: 0,
  };

  return (
    <div className="space-y-6">
      {/* ── Top Bar & Breadcrumb Navigation ────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 shadow-2xs transition-colors cursor-pointer"
          >
            <span>←</span>
            <span>All Companies</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-400">/</span>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight flex items-center gap-2">
              <span>{company?.company_name || 'Company Details'}</span>
              {company?.short_code && (
                <CompanyHoverBadge
                  shortCode={company.short_code}
                  companyId={companyId}
                  company={{
                    id: companyId,
                    short_code: company.short_code,
                    company_name: company.company_name,
                    factory_address: company.factory_address,
                    contact_person: company.contact_person,
                    communication_email: company.communication_email,
                  }}
                >
                  <ShortCodeTag code={company.short_code} showIcon={false} />
                </CompanyHoverBadge>
              )}
            </h2>
          </div>
        </div>

        {/* Export */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportCSV}
            disabled={!certificates.length}
            className="btn btn-secondary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 disabled:opacity-40 cursor-pointer"
            title="Download company certificate report as CSV"
          >
            <span>📥</span>
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* ── Company Metrics Cards ──────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-3.5 border-l-4 border-l-slate-700 bg-white"
        >
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total In Period</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{summary.total}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Across all cert types</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 }}
          className="card p-3.5 border-l-4 border-l-emerald-600 bg-white"
        >
          <p className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider">🎓 Training</p>
          <p className="text-2xl font-bold text-emerald-700 mt-0.5">{summary.training}</p>
          <p className="text-[10px] text-emerald-600 mt-0.5">Safety certificates</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="card p-3.5 border-l-4 border-l-brand-600 bg-white"
        >
          <p className="text-[11px] font-semibold text-brand-800 uppercase tracking-wider">📋 Form 9</p>
          <p className="text-2xl font-bold text-brand-700 mt-0.5">{summary.form9}</p>
          <p className="text-[10px] text-brand-600 mt-0.5">Hoists and lifts</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="card p-3.5 border-l-4 border-l-brand-600 bg-white"
        >
          <p className="text-[11px] font-semibold text-brand-800 uppercase tracking-wider">📋 Form 10</p>
          <p className="text-2xl font-bold text-brand-700 mt-0.5">{summary.form10}</p>
          <p className="text-[10px] text-brand-600 mt-0.5">Lifting equipment</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="card p-3.5 border-l-4 border-l-purple-600 bg-white"
        >
          <p className="text-[11px] font-semibold text-purple-800 uppercase tracking-wider">🧪 Form 11</p>
          <p className="text-2xl font-bold text-purple-700 mt-0.5">{summary.form11}</p>
          <p className="text-[10px] text-purple-600 mt-0.5">Pressure vessels</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          className="card p-3.5 border-l-4 border-l-blue-500 bg-white col-span-2 sm:col-span-1"
        >
          <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Health Status</p>
          <div className="flex items-center gap-1.5 mt-1">
            <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              {summary.compliant} Valid
            </span>
            {summary.expiring_soon > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                {summary.expiring_soon} Exp
              </span>
            )}
            {summary.overdue > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-red-50 text-red-700 border border-red-200">
                {summary.overdue} Ovd
              </span>
            )}
          </div>
        </motion.div>
      </div>

      {/* ── Filters & Search Bar for This Company ─────────────── */}
      <div className="card p-4 sm:p-5 space-y-4 bg-white shadow-sm border border-gray-200">
        {/* Certificate Type Segmented Pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider mr-1">Section:</span>
            <button
              type="button"
              onClick={() => setCertType('ALL')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                certType === 'ALL'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All Types ({summary.total})
            </button>
            <button
              type="button"
              onClick={() => setCertType('TRAINING')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'TRAINING'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200/80'
              }`}
            >
              <span>🎓</span>
              <span>Training ({summary.training})</span>
            </button>
            <button
              type="button"
              onClick={() => setCertType('FORM_9')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_9'
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-brand-50 text-brand-800 hover:bg-brand-100 border border-brand-200/80'
              }`}
            >
              <span>📋</span>
              <span>Form 9 ({summary.form9})</span>
            </button><button
              type="button"
              onClick={() => setCertType('FORM_10')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_10'
                  ? 'bg-brand-600 text-white shadow-xs'
                  : 'bg-brand-50 text-brand-800 hover:bg-brand-100 border border-brand-200/80'
              }`}
            >
              <span>📋</span>
              <span>Form 10 ({summary.form10})</span>
            </button>
            <button
              type="button"
              onClick={() => setCertType('FORM_11')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                certType === 'FORM_11'
                  ? 'bg-purple-600 text-white shadow-xs'
                  : 'bg-purple-50 text-purple-800 hover:bg-purple-100 border border-purple-200/80'
              }`}
            >
              <span>🧪</span>
              <span>Form 11 ({summary.form11})</span>
            </button>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="input text-xs py-1.5 px-2.5 bg-white cursor-pointer font-medium"
            >
              <option value="ALL">All Statuses</option>
              <option value="COMPLIANT">Valid Only</option>
              <option value="EXPIRING_SOON">Expiring Soon Only</option>
              <option value="OVERDUE">Overdue Only</option>
            </select>
          </div>
        </div>

        {/* Date & Search Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Financial Year</label>
            <select
              value={financialYear}
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              className="input text-xs py-2 w-full bg-white cursor-pointer font-medium"
            >
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy.value} value={fy.value}>
                  {fy.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setFinancialYear('ALL');
              }}
              className="input text-xs py-2 w-full bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setFinancialYear('ALL');
              }}
              className="input text-xs py-2 w-full bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Search Certificates</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Cert #, equipment, trainee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input text-xs py-2 pl-8 pr-3 w-full bg-white"
              />
              <span className="absolute left-2.5 top-2.5 text-gray-400 text-xs">🔍</span>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-xs"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Date Presets */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[11px] font-semibold text-gray-400 mr-1">Quick Intervals:</span>
          <button
            type="button"
            onClick={() => handleQuickPreset('month')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors cursor-pointer"
          >
            This Month
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('30d')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors cursor-pointer"
          >
            Last 30 Days
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('90d')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors cursor-pointer"
          >
            Last 90 Days
          </button>
          <button
            type="button"
            onClick={() => handleQuickPreset('fy')}
            className="px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium transition-colors cursor-pointer"
          >
            Current FY
          </button>
          {(startDate || endDate || financialYear !== 'ALL' || statusFilter !== 'ALL' || searchQuery) && (
            <button
              type="button"
              onClick={() => {
                handleQuickPreset('all');
                setStatusFilter('ALL');
                setSearchQuery('');
              }}
              className="px-2 py-0.5 rounded bg-red-50 hover:bg-red-100 text-red-600 text-[11px] font-medium transition-colors ml-1 cursor-pointer"
            >
              Clear Filters ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Error Banner ────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700 flex items-center justify-between">
          <span>⚠️ {error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-500 font-bold ml-2">✕</button>
        </div>
      )}

      {/* ── Certificates Table for this Company ────────────────── */}
      <div className="card overflow-hidden bg-white shadow-sm border border-gray-200 rounded-xl">
        <div className="p-4 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">
              {certType === 'ALL'
                ? 'All Certificates'
                : certType === 'TRAINING'
                ? 'Training Certificates'
                : certType === 'FORM_9' ? 'Form 9 Certificates' : certType === 'FORM_10'
                ? 'Form 10 Certificates'
                : 'Form 11 Reports'}
            </span>
            <span className="text-xs bg-slate-200/80 text-slate-700 px-2 py-0.5 rounded-full font-semibold">
              {certificates.length} {certificates.length === 1 ? 'record' : 'records'}
            </span>
          </div>
          <span className="text-[11px] text-gray-500">
            Sorted by examination/issue date descending
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : paginatedCertificates.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="text-3xl mb-2">📜</p>
            <p className="font-semibold text-gray-800 text-sm">No certificates found</p>
            <p className="text-xs text-gray-400 mt-1">
              No certificates match the selected filters for this company.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto no-scrollbar">
            <table className="table w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <tr>
                  <th className="py-3 px-4 text-left">Type</th>
                  <th className="py-3 px-3 text-left">Cert / Report #</th>
                  <th className="py-3 px-4 text-left">Equipment / Topic</th>
                  <th className="py-3 px-3 text-left">Exam / Issue Date</th>
                  <th className="py-3 px-3 text-left">Valid Upto / Next Due</th>
                  <th className="py-3 px-3 text-center">Status</th>
                  <th className="py-3 px-3 text-left">Inspector / Faculty</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedCertificates.map((cert) => {
                  const typeBadge =
                    cert.type === 'TRAINING' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <span>🎓</span>
                        <span>Training</span>
                      </span>
                    ) : cert.type === 'FORM_9' || cert.type === 'FORM_10' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-brand-50 text-brand-800 border border-brand-200">
                        <span>📋</span>
                        <span>{cert.type === 'FORM_9' ? 'Form 9' : 'Form 10'}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-800 border border-purple-200">
                        <span>🧪</span>
                        <span>Form 11</span>
                      </span>
                    );

                  const statusBadge =
                    cert.status === 'COMPLIANT' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <span>✓</span>
                        <span>Valid</span>
                      </span>
                    ) : cert.status === 'EXPIRING_SOON' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-900 border border-amber-200">
                        <span>⚠️</span>
                        <span>Expiring ({cert.days_until_due}d)</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold bg-red-50 text-red-800 border border-red-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                        <span>Overdue ({Math.abs(cert.days_until_due)}d)</span>
                      </span>
                    );

                  return (
                    <tr key={cert.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Type */}
                      <td className="py-3 px-4 whitespace-nowrap">{typeBadge}</td>

                      {/* Certificate Number */}
                      <td className="py-3 px-3 whitespace-nowrap font-mono font-bold text-gray-900">
                        <button
                          type="button"
                          onClick={() => handleOpenView(cert)}
                          className="hover:text-brand-600 transition-colors text-left cursor-pointer underline-offset-2 hover:underline"
                          title="Click to view certificate in place"
                        >
                          {cert.certificate_number}
                        </button>
                      </td>

                      {/* Equipment / Topic / Trainee */}
                      <td className="py-3 px-4">
                        <p className="font-semibold text-gray-900 leading-tight">
                          {cert.equipment_name}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-mono">
                          {cert.type === 'TRAINING'
                            ? `Trainee: ${cert.trainee_name}`
                            : cert.equipment_identifier}
                        </p>
                      </td>

                      {/* Examination Date */}
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                        {cert.issue_or_exam_date}
                      </td>

                      {/* Expiry Date */}
                      <td className="py-3 px-3 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{cert.expiry_or_due_date}</span>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-3 text-center whitespace-nowrap">{statusBadge}</td>

                      {/* Inspector / Faculty */}
                      <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                        {cert.inspector_or_trainer}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleOpenView(cert)}
                          className="text-xs font-semibold text-brand-700 hover:text-brand-900 bg-brand-50 hover:bg-brand-100 px-3 py-1 rounded-md transition-colors cursor-pointer"
                          title="View certificate in place"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={certificates.length}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* ── Direct PDF Preview Modal in Place ───────────────────── */}
      <Modal
        open={viewModalOpen}
        onClose={handleCloseView}
        title={
          selectedCert
            ? `${
                selectedCert.type === 'TRAINING'
                  ? 'Training Certificate'
                  : selectedCert.type === 'FORM_9' ? 'Form 9 Certificate' : selectedCert.type === 'FORM_10'
                  ? 'Form 10 Certificate'
                  : 'Form 11 Report'
              } — ${selectedCert.certificate_number}`
            : 'Certificate Preview'
        }
        size="xl"
      >
        {selectedCert && (
          <div className="space-y-3">
            {/* Top action header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-slate-50 p-2.5 sm:p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                    selectedCert.type === 'TRAINING'
                      ? 'bg-emerald-100 text-emerald-800'
                      : selectedCert.type === 'FORM_10'
                      ? 'bg-brand-100 text-brand-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}
                >
                  {selectedCert.type === 'TRAINING'
                    ? '🎓 Training'
                    : selectedCert.type === 'FORM_9' ? '📋 Form 9' : selectedCert.type === 'FORM_10'
                    ? '📋 Form 10'
                    : '🧪 Form 11'}
                </span>
                <span className="text-xs font-mono font-bold text-gray-900">
                  {selectedCert.certificate_number}
                </span>
                {company?.company_name && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-xs text-gray-700 font-medium truncate max-w-xs" title={company.company_name}>
                      {company.company_name}
                    </span>
                    {company.short_code && (
                      <CompanyHoverBadge
                        shortCode={company.short_code}
                        companyId={companyId}
                        company={{
                          id: companyId,
                          short_code: company.short_code,
                          company_name: company.company_name,
                          factory_address: company.factory_address,
                          contact_person: company.contact_person,
                          communication_email: company.communication_email,
                        }}
                      >
                        <ShortCodeTag code={company.short_code} showIcon={false} />
                      </CompanyHoverBadge>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleDownloadPdf(selectedCert)}
                  disabled={downloadingPdf}
                  className="btn btn-primary text-xs py-1 px-2.5 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>{downloadingPdf ? 'Downloading...' : 'Download PDF'}</span>
                </button>

                {pdfBlobUrl && (
                  <a
                    href={pdfBlobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-xs py-1 px-2.5 flex items-center gap-1.5"
                    title="Open in new window"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>New Tab ↗</span>
                  </a>
                )}

                <Link
                  to={
                    selectedCert.type === 'TRAINING'
                      ? `/training?search=${encodeURIComponent(selectedCert.certificate_number)}`
                      : selectedCert.type === 'FORM_9' ? `/form9?search=${encodeURIComponent(selectedCert.certificate_number)}` : selectedCert.type === 'FORM_10'
                      ? `/form10?search=${encodeURIComponent(selectedCert.certificate_number)}`
                      : `/form11?search=${encodeURIComponent(selectedCert.certificate_number)}`
                  }
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md transition-colors inline-flex items-center gap-1"
                  title="Open full management page"
                >
                  <span>Open in {selectedCert.type === 'TRAINING' ? 'Training' : selectedCert.type === 'FORM_9' ? 'Form 9' : selectedCert.type === 'FORM_10' ? 'Form 10' : 'Form 11'}</span>
                  <span>↗</span>
                </Link>
              </div>
            </div>

            {/* Key info pill row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-white p-2.5 rounded-lg border border-gray-100">
              <div>
                <span className="text-gray-400 block text-[11px]">
                  {selectedCert.type === 'TRAINING' ? 'Topic / Course' : 'Equipment / Vessel'}
                </span>
                <span className="font-semibold text-gray-800 truncate block">
                  {selectedCert.equipment_name || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-[11px]">
                  {selectedCert.type === 'TRAINING' ? 'Trainee' : 'Tag / Serial #'}
                </span>
                <span className="font-semibold text-gray-800 font-mono truncate block">
                  {selectedCert.type === 'TRAINING'
                    ? selectedCert.trainee_name || '—'
                    : selectedCert.equipment_identifier || '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-400 block text-[11px]">Exam / Issue Date</span>
                <span className="font-semibold text-gray-800">{selectedCert.issue_or_exam_date}</span>
              </div>
              <div>
                <span className="text-gray-400 block text-[11px]">Valid Upto / Due</span>
                <span className="font-semibold text-gray-800">{selectedCert.expiry_or_due_date}</span>
              </div>
            </div>

            {/* Document preview iframe or states */}
            {viewLoading ? (
              <div className="py-24 flex flex-col items-center justify-center space-y-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-brand-600" />
                <p className="text-sm font-medium text-gray-600">
                  Loading {selectedCert.type === 'TRAINING' ? 'Training Certificate' : selectedCert.type === 'FORM_9' ? 'Form 9' : selectedCert.type === 'FORM_10' ? 'Form 10' : 'Form 11'} PDF document...
                </p>
                <span className="text-xs text-gray-400">Fetching document from server</span>
              </div>
            ) : viewError ? (
              <div className="py-14 text-center space-y-3 bg-red-50 rounded-lg border border-red-200 p-6">
                <p className="text-2xl">⚠️</p>
                <p className="text-sm font-semibold text-red-800">{viewError}</p>
                <p className="text-xs text-red-600">Failed to load PDF preview in place.</p>
                <div className="pt-2 flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleOpenView(selectedCert)}
                    className="btn btn-primary text-xs py-1.5 px-4"
                  >
                    Retry
                  </button>
                  <Link
                    to={
                      selectedCert.type === 'TRAINING'
                        ? `/training?search=${encodeURIComponent(selectedCert.certificate_number)}`
                        : selectedCert.type === 'FORM_9' ? `/form9?search=${encodeURIComponent(selectedCert.certificate_number)}` : selectedCert.type === 'FORM_10'
                        ? `/form10?search=${encodeURIComponent(selectedCert.certificate_number)}`
                        : `/form11?search=${encodeURIComponent(selectedCert.certificate_number)}`
                    }
                    className="btn btn-secondary text-xs py-1.5 px-4"
                  >
                    Open Page Instead ↗
                  </Link>
                </div>
              </div>
            ) : pdfBlobUrl ? (
              <iframe
                src={pdfBlobUrl}
                className="w-full h-[72vh] rounded-lg border border-slate-300 shadow-inner bg-slate-100"
                title={`${selectedCert.type} PDF Document — ${selectedCert.certificate_number}`}
              />
            ) : (
              <div className="py-16 text-center text-gray-400 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                PDF document preview unavailable.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
